import React from 'react';
import styled from 'styled-components';
import Footer from '../../Footer/Footer';

const TermsContainer = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin: 2rem 1rem;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.4rem;
    margin: 1.5rem 0 1rem;
  }

  p {
    line-height: 1.6;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;

    li {
      line-height: 1.6;
      margin-bottom: 0.5rem;
      color: ${props => props.theme.colors.text};
    }
  }
`;

const FarmShopTerms = () => {
  return (
    <TermsContainer>
      <ContentContainer>
        <Title>Belvoir Farm Shop - Terms and Conditions</Title>
        
        <Section>
          <h2>1. General Terms</h2>
          <p>
            Welcome to Belvoir Farm Shop. By placing an order through our online shop, you agree to be bound by these Terms and Conditions. 
            Please read them carefully before placing your order. These terms apply to all purchases made through the Belvoir Farm Shop website.
          </p>
          <p>
            Belvoir Farm Shop is operated by Belvoir Castle. Our registered address is Belvoir Castle, Grantham, Leicestershire, NG32 1PE.
          </p>
        </Section>

        <Section>
          <h2>2. Ordering and Payment</h2>
          <p>
            When you place an order with Belvoir Farm Shop, you agree to the following terms:
          </p>
          <ul>
            <li>All orders are subject to product availability and acceptance by us.</li>
            <li>We reserve the right to refuse or cancel any order at our discretion.</li>
            <li>Prices displayed on our website are in British Pounds (GBP) and include VAT where applicable.</li>
            <li>All prices are correct at the time of publishing but may be subject to change without prior notice.</li>
            <li>We accept payment by credit/debit card and other payment methods as displayed during checkout.</li>
            <li>Payment must be received in full before we can process and prepare your order.</li>
            <li>We use secure payment processing to protect your payment information.</li>
            <li>If we are unable to process your payment, your order will not be fulfilled.</li>
            <li>You will receive an email confirmation once your order and payment have been successfully processed.</li>
          </ul>
        </Section>

        <Section>
          <h2>3. Click & Collect Service</h2>
          <p>
            Currently, Belvoir Farm Shop operates a Click & Collect service only. The following terms apply:
          </p>
          <ul>
            <li>Orders can be collected from Belvoir Farm Shop during our opening hours.</li>
            <li>You will be notified by email when your order is ready for collection.</li>
            <li>Orders are typically ready for collection within 2-3 working days, but this may vary depending on product availability.</li>
            <li>You must collect your order within 7 days of being notified that it is ready.</li>
            <li>When collecting your order, please bring a valid form of identification and your order confirmation email (either printed or on your phone).</li>
            <li>If you are unable to collect the order yourself, you may authorise someone else to collect it on your behalf. They must bring your order confirmation email and identification.</li>
            <li>We reserve the right to ask for proof of identity before releasing your order.</li>
            <li>If your order is not collected within 7 days of notification, we may contact you to arrange an alternative collection time. Uncollected orders may be subject to storage fees or cancellation.</li>
            <li>Collection address: Belvoir Farm Shop, Belvoir Castle, Grantham, Leicestershire, NG32 1PE.</li>
          </ul>
        </Section>

        <Section>
          <h2>4. Delivery Service</h2>
          <p>
            Delivery service is currently not available. We are working to introduce delivery options in the future. 
            When delivery becomes available, separate delivery terms and conditions will apply, and they will be clearly displayed on our website.
          </p>
        </Section>

        <Section>
          <h2>5. Product Information and Stock Availability</h2>
          <p>
            We strive to ensure product information is accurate, but please note:
          </p>
          <ul>
            <li>Product images are for illustrative purposes only and may not reflect the exact appearance of the product you receive.</li>
            <li>All product descriptions, weights, and measurements are approximate.</li>
            <li>Products are subject to availability. If a product you have ordered is out of stock, we will contact you to arrange a substitute, partial refund, or order cancellation.</li>
            <li>We reserve the right to limit quantities of products purchased per person, per household, or per order.</li>
            <li>We cannot guarantee that all products shown on our website will be available at the time of collection.</li>
            <li>For perishable items, including fresh produce, meat, and dairy products, we recommend collecting as soon as possible after receiving notification.</li>
            <li>We source our products from local suppliers where possible, and availability may vary seasonally.</li>
          </ul>
        </Section>

        <Section>
          <h2>6. Returns and Refunds</h2>
          <p>
            Our returns and refunds policy is as follows:
          </p>
          <h3>6.1 Your Right to Cancel</h3>
          <p>
            Under UK Consumer Contracts Regulations, you have the right to cancel your order and receive a full refund within 14 days of the day after you collect your order. 
            This right does not apply to certain products as outlined below.
          </p>
          <h3>6.2 Products Excluded from Returns</h3>
          <ul>
            <li>Perishable goods (fresh produce, meat, fish, dairy products, etc.)</li>
            <li>Sealed products that have been opened and cannot be returned for hygiene or health protection reasons</li>
            <li>Personalised or bespoke items made to your specification</li>
          </ul>
          <h3>6.3 Return Process</h3>
          <ul>
            <li>To cancel an order, please contact us within 14 days of collection using the contact details provided below.</li>
            <li>Returned products must be in their original condition, unused, and in their original packaging where applicable.</li>
            <li>You are responsible for the cost of returning products to us unless the item is faulty or incorrectly supplied.</li>
            <li>We recommend using a tracked delivery service when returning items, as we cannot be responsible for items lost in transit.</li>
            <li>Once we receive and inspect the returned item, we will process your refund within 14 days.</li>
            <li>Refunds will be issued to the original payment method used for the purchase.</li>
          </ul>
          <h3>6.4 Faulty or Incorrect Products</h3>
          <ul>
            <li>If you receive a faulty or incorrect product, please contact us immediately.</li>
            <li>We will arrange for collection or provide instructions for return at our expense.</li>
            <li>You will receive a full refund or replacement for faulty or incorrectly supplied items.</li>
            <li>For perishable items, please check your order at the time of collection. Any issues should be reported immediately.</li>
          </ul>
        </Section>

        <Section>
          <h2>7. Order Cancellations and Changes</h2>
          <p>
            The following terms apply to order cancellations and changes:
          </p>
          <ul>
            <li>You may cancel your order before it has been processed and prepared. Please contact us immediately if you wish to cancel.</li>
            <li>Once an order has been processed and prepared for collection, cancellation may not be possible, and our returns policy will apply.</li>
            <li>If you wish to make changes to your order, please contact us as soon as possible. We cannot guarantee that changes can be accommodated.</li>
            <li>We reserve the right to cancel orders if products are unavailable, if there are errors in pricing or product information, or for any other reason at our discretion.</li>
            <li>If we cancel your order after payment has been processed, you will receive a full refund within 14 days.</li>
            <li>We will notify you by email if we need to cancel your order.</li>
          </ul>
        </Section>

        <Section>
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law:
          </p>
          <ul>
            <li>Belvoir Farm Shop and Belvoir Castle shall not be liable for any indirect, special, incidental, or consequential damages arising from your use of our website or products.</li>
            <li>Our total liability for any claim relating to your order shall not exceed the total value of the order in question.</li>
            <li>We do not exclude or limit our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded by law.</li>
            <li>We are not responsible for any damage or loss that may occur after you have collected your order, except in cases of faulty or incorrectly supplied products.</li>
          </ul>
        </Section>

        <Section>
          <h2>9. Privacy and Data Protection</h2>
          <p>
            We take your privacy seriously and are committed to protecting your personal data. When you place an order:
          </p>
          <ul>
            <li>We collect and process your personal information in accordance with our Privacy Policy and applicable data protection laws.</li>
            <li>Your personal information will be used to process your order, communicate with you about your order, and for other purposes as outlined in our Privacy Policy.</li>
            <li>We do not sell your personal information to third parties.</li>
            <li>Payment information is processed securely through our payment service provider and is not stored by us.</li>
            <li>For more information about how we handle your personal data, please see our Privacy Policy.</li>
          </ul>
        </Section>

        <Section>
          <h2>10. Intellectual Property</h2>
          <p>
            All content on the Belvoir Farm Shop website, including text, graphics, logos, images, and software, is the property of Belvoir Castle or its content suppliers and is protected by copyright and other intellectual property laws. 
            You may not reproduce, distribute, or use any content from our website without our prior written permission.
          </p>
        </Section>

        <Section>
          <h2>11. Governing Law</h2>
          <p>
            These Terms and Conditions are governed by and construed in accordance with the laws of England and Wales. 
            Any disputes arising from these terms or your use of our website shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </Section>

        <Section>
          <h2>12. Changes to Terms and Conditions</h2>
          <p>
            We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to our website. 
            It is your responsibility to review these terms regularly. Continued use of our website and services after changes constitutes acceptance of the modified terms.
          </p>
        </Section>

        <Section>
          <h2>13. Contact Us</h2>
          <p>
            If you have any questions, concerns, or complaints about these Terms and Conditions or your order, please contact us:
          </p>
          <p>
            <strong>Belvoir Farm Shop</strong><br />
            Belvoir Castle<br />
            Grantham<br />
            Leicestershire<br />
            NG32 1PE<br /><br />
            <strong>Email:</strong> <a href="mailto:info@belvoircastle.com">info@belvoircastle.com</a><br />
            <strong>Phone:</strong> +44 (0)1476 871001<br />
            <strong>Website:</strong> <a href="/farm-shop">www.belvoircastle.com/farm-shop</a>
          </p>
          <p>
            Our customer service team is available to assist you with any queries regarding your order or our products.
          </p>
        </Section>

        <Section>
          <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic', marginTop: '3rem' }}>
            Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </Section>
      </ContentContainer>
      <Footer />
    </TermsContainer>
  );
};

export default FarmShopTerms;

